import React, { useState, useEffect } from 'react';
import { Eye, Edit, CheckCircle2, XCircle, Loader2, AlertCircle, Download, FileSpreadsheet, FileText, History } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  { value: 'processing', label: 'Processing', color: 'bg-purple-100 text-purple-700' },
  { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
];

const statusColor = (s) => (STATUSES.find(x => x.value === s)?.color) || 'bg-slate-100 text-slate-600';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending_approval | active
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 'confirmed', tracking_number: '', status_notes: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, order: null, reason: '' });
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`, auth());
      setOrders(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openView = async (o) => {
    setSelected(o);
    setViewOpen(true);
    setAuditLoading(true);
    setAuditEntries([]);
    try {
      const res = await axios.get(`${API}/admin/orders/${o.order_id}/audit`, auth());
      setAuditEntries(res.data || []);
    } catch (e) {
      // non-fatal
    } finally {
      setAuditLoading(false);
    }
  };
  const openEdit = (o) => {
    setSelected(o);
    setStatusForm({ status: o.status || 'confirmed', tracking_number: o.tracking_number || '', status_notes: o.status_notes || '' });
    setEditOpen(true);
  };

  const openApproveDialog = (order) => setActionDialog({ open: true, type: 'approve', order, reason: '' });
  const openRejectDialog = (order) => setActionDialog({ open: true, type: 'reject', order, reason: '' });
  const closeActionDialog = () => setActionDialog({ open: false, type: null, order: null, reason: '' });

  const submitAction = async () => {
    const { type, order, reason } = actionDialog;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/orders/${order.order_id}/${type}`,
        { reason: reason || null },
        auth()
      );
      toast({ title: type === 'approve' ? 'Order approved' : 'Order rejected', description: order.order_id });
      closeActionDialog();
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || `Failed to ${type}`, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/orders/${selected.order_id}/status`, statusForm, auth());
      toast({ title: 'Order updated', description: `${selected.order_id} → ${statusForm.status}` });
      setEditOpen(false);
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = (order) => openApproveDialog(order);
  const handleReject = (order) => openRejectDialog(order);

  const downloadExcel = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/export.xlsx`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export downloaded' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to export orders', variant: 'destructive' });
    }
  };

  const downloadPdf = (order, kind) => {
    // kind: 'confirmation' | 'invoice'
    const url = `${API}/orders/${order.order_id}/${kind}.pdf`;
    // Use fetch with auth header to get a blob since browser <a> won't send it
    fetch(url, auth())
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${kind === 'invoice' ? 'invoice' : 'order'}-${order.order_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: 'Error', description: `Could not download ${kind} PDF`, variant: 'destructive' }));
  };

  const pendingCount = orders.filter(o => o.status === 'pending_approval').length;
  const activeCount = orders.filter(o => ['confirmed', 'processing', 'shipped'].includes(o.status)).length;

  const filtered = orders.filter(o => {
    if (filter === 'pending_approval') return o.status === 'pending_approval';
    if (filter === 'active') return ['confirmed', 'processing', 'shipped'].includes(o.status);
    return true;
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-purple-600" /></div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="admin-orders-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-800">Orders Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadExcel} data-testid="export-orders-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Export to Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="border-2"><CardContent className="p-3 sm:p-4 text-center"><p className="text-2xl sm:text-3xl font-bold text-slate-700">{orders.length}</p><p className="text-xs text-slate-500">Total</p></CardContent></Card>
        <Card className="border-2 cursor-pointer" onClick={() => setFilter('pending_approval')} data-testid="filter-pending-approval"><CardContent className="p-3 sm:p-4 text-center"><p className="text-2xl sm:text-3xl font-bold text-yellow-600">{pendingCount}</p><p className="text-xs text-slate-500">Pending Approval</p></CardContent></Card>
        <Card className="border-2 cursor-pointer" onClick={() => setFilter('active')}><CardContent className="p-3 sm:p-4 text-center"><p className="text-2xl sm:text-3xl font-bold text-blue-600">{activeCount}</p><p className="text-xs text-slate-500">Active</p></CardContent></Card>
        <Card className="border-2 cursor-pointer" onClick={() => setFilter('all')}><CardContent className="p-3 sm:p-4 text-center"><p className="text-2xl sm:text-3xl font-bold text-purple-600">All</p><p className="text-xs text-slate-500">Show all</p></CardContent></Card>
      </div>

      {filter !== 'all' && (
        <div className="mb-4">
          <Button variant="outline" size="sm" onClick={() => setFilter('all')}>Clear filter</Button>
          <span className="ml-2 text-sm text-slate-500">Filter: {filter.replace(/_/g, ' ')}</span>
        </div>
      )}

      <Card className="border-2">
        <CardHeader><CardTitle>Orders ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No orders match this filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Order ID</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Items</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order, idx) => {
                    const si = order.shipping_info || {};
                    const customerName = `${si.firstName || si.first_name || ''} ${si.lastName || si.last_name || ''}`.trim();
                    const isPending = order.status === 'pending_approval';
                    return (
                      <tr key={`${order.order_id}-${idx}`} className="border-t hover:bg-slate-50" data-testid={`admin-order-${order.order_id}`}>
                        <td className="p-3 font-medium text-slate-800">
                          {order.order_id}
                          {order.is_guest === false && <span className="ml-1 text-[10px] uppercase bg-purple-100 text-purple-700 px-1 py-0.5 rounded">B2B</span>}
                        </td>
                        <td className="p-3 text-slate-600">
                          <div>{customerName || si.email}</div>
                          {(order.user_company || si.company) && <div className="text-[11px] text-slate-400 capitalize">{order.user_company || si.company}</div>}
                        </td>
                        <td className="p-3 text-slate-600">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</td>
                        <td className="p-3 text-slate-600">{(order.items || []).length}</td>
                        <td className="p-3 font-semibold text-blue-600">${Number(order.total || 0).toFixed(2)}</td>
                        <td className="p-3">
                          <span className={`px-3 py-1 rounded-full text-xs capitalize ${statusColor(order.status)}`}>{(order.status || '').replace(/_/g, ' ')}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => openView(order)} data-testid={`view-order-${order.order_id}`}><Eye className="h-3 w-3" /></Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(order)}><Edit className="h-3 w-3" /></Button>
                            <Button size="sm" variant="outline" onClick={() => downloadPdf(order, 'invoice')} title="Download invoice PDF" data-testid={`download-invoice-${order.order_id}`}>
                              <FileText className="h-3 w-3" />
                            </Button>
                            {isPending && (
                              <>
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApprove(order)} disabled={actionLoading} data-testid={`approve-${order.order_id}`}>
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Approve
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleReject(order)} disabled={actionLoading} data-testid={`reject-${order.order_id}`}>
                                  <XCircle className="h-3 w-3 mr-1" />Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Order Details — {selected?.order_id}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-500">Status</Label><p className="capitalize font-semibold">{(selected.status || '').replace(/_/g, ' ')}</p></div>
                <div><Label className="text-slate-500">Total</Label><p className="text-blue-600 font-semibold">${Number(selected.total || 0).toFixed(2)}</p></div>
                <div><Label className="text-slate-500">Date</Label><p>{selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}</p></div>
                <div><Label className="text-slate-500">Payment</Label><p className="capitalize">{(selected.payment_method || '').replace(/_/g, ' ')}</p></div>
                {selected.tracking_number && <div className="col-span-2"><Label className="text-slate-500">Tracking</Label><p>{selected.tracking_number}</p></div>}
                {selected.user_company && <div className="col-span-2"><Label className="text-slate-500">Buyer Organization</Label><p className="capitalize">{selected.user_company}</p></div>}
              </div>

              <div>
                <Label className="text-slate-500">Shipping Information</Label>
                <div className="bg-slate-50 p-3 rounded mt-1">
                  <p className="font-medium">{selected.shipping_info?.firstName || selected.shipping_info?.first_name} {selected.shipping_info?.lastName || selected.shipping_info?.last_name}</p>
                  <p className="text-slate-600">{selected.shipping_info?.email} · {selected.shipping_info?.phone}</p>
                  <p className="text-slate-600">{selected.shipping_info?.address}, {selected.shipping_info?.city}, {selected.shipping_info?.state} {selected.shipping_info?.zip_code || selected.shipping_info?.zipCode}</p>
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Items</Label>
                <div className="space-y-1 mt-1">
                  {(selected.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between bg-slate-50 p-2 rounded">
                      <span>{it.name} × {it.quantity}</span>
                      <span className="font-medium">${(Number(it.price) * Number(it.quantity)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selected.status === 'pending_approval' && (
                <div className="flex gap-2 pt-3 border-t">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setViewOpen(false); openApproveDialog(selected); }}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />Approve
                  </Button>
                  <Button variant="outline" className="text-red-600" onClick={() => { setViewOpen(false); openRejectDialog(selected); }}>
                    <XCircle className="h-4 w-4 mr-1" />Reject
                  </Button>
                </div>
              )}

              <div className="flex gap-2 flex-wrap pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => downloadPdf(selected, 'confirmation')}>
                  <Download className="h-4 w-4 mr-1" />Confirmation PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(selected, 'invoice')}>
                  <FileText className="h-4 w-4 mr-1" />Invoice PDF
                </Button>
              </div>

              {/* Audit Log */}
              <div className="pt-3 border-t" data-testid="audit-log-section">
                <Label className="text-slate-500 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Audit Log</Label>
                {auditLoading ? (
                  <p className="text-xs text-slate-400 mt-1">Loading…</p>
                ) : auditEntries.length === 0 ? (
                  <p className="text-xs text-slate-400 mt-1">No actions recorded yet.</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {auditEntries.map((e, i) => (
                      <div key={i} className="bg-slate-50 p-2 rounded text-xs" data-testid={`audit-entry-${i}`}>
                        <div className="flex justify-between">
                          <span className="font-semibold capitalize">{e.action.replace(/_/g, ' ')}</span>
                          <span className="text-slate-400">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                        </div>
                        <div className="text-slate-600">by {e.admin_name || e.admin_email}</div>
                        {e.reason && <div className="italic text-slate-500 mt-1">"{e.reason}"</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Status Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Status — {selected?.order_id}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tracking Number</Label>
              <Input value={statusForm.tracking_number} onChange={(e) => setStatusForm({ ...statusForm, tracking_number: e.target.value })} />
            </div>
            <div>
              <Label>Status Notes</Label>
              <Textarea rows={3} value={statusForm.status_notes} onChange={(e) => setStatusForm({ ...statusForm, status_notes: e.target.value })} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateStatus} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={actionLoading}>Update</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve / Reject confirmation dialog with reason */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => { if (!open) closeActionDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'approve' ? 'Approve order' : 'Reject order'} — {actionDialog.order?.order_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {actionDialog.type === 'approve'
                ? 'Approving will confirm the order and email the buyer with an invoice PDF.'
                : 'Rejecting will notify the buyer and mark the order as rejected. A reason is recommended.'}
            </p>
            <div>
              <Label>{actionDialog.type === 'approve' ? 'Approval note (optional)' : 'Reason (recommended)'}</Label>
              <Textarea
                rows={3}
                value={actionDialog.reason}
                onChange={(e) => setActionDialog({ ...actionDialog, reason: e.target.value })}
                placeholder={actionDialog.type === 'approve' ? 'e.g. Confirmed PO #12345, ship Friday' : 'e.g. Out of stock for SKU 1234, please re-order with substitute'}
                data-testid="action-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeActionDialog}>Cancel</Button>
              <Button
                className={actionDialog.type === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                onClick={submitAction}
                disabled={actionLoading}
                data-testid="action-confirm-btn"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (actionDialog.type === 'approve' ? 'Approve & send invoice' : 'Reject & notify')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {pendingCount > 0 && filter !== 'pending_approval' && (
        <div className="fixed bottom-4 right-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3 shadow-lg flex items-center gap-2 max-w-xs">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">{pendingCount} order{pendingCount > 1 ? 's' : ''} awaiting approval</p>
            <button className="text-xs text-yellow-700 underline" onClick={() => setFilter('pending_approval')}>Review now</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
